import React, { useEffect, useMemo, useState } from "react";
import api, { API, formatIDR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowsLeftRight, UploadSimple, FileArrowDown, CheckCircle, Warning,
  ArrowDown, ArrowUp, Sparkle, Trash, Bank,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const ConfBadge = ({ conf }) => {
  if (conf == null) return null;
  const pct = Math.round(conf * 100);
  const cls = pct >= 90 ? "bg-emerald-100 text-emerald-700" : pct >= 70 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700";
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${cls}`}>{pct}%</span>;
};

export default function BankReconciliation() {
  const [accounts, setAccounts] = useState([]);
  const [cbId, setCbId] = useState("");
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState({}); // row_idx -> bool
  const [applying, setApplying] = useState(false);
  const [appliedSummary, setAppliedSummary] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [autoApply, setAutoApply] = useState(true);
  const [autoApplied, setAutoApplied] = useState(null); // {count, failed, total_amount, items}

  useEffect(() => {
    api.get("/cashbank/accounts").then((r) => {
      setAccounts(r.data || []);
      if (r.data?.length) setCbId(r.data[0].id);
    }).catch(() => {});
  }, []);

  const downloadSample = () => {
    const csv = "Tanggal,Keterangan,Debet,Kredit\n2026-10-01,TRSF E-BANKING PT PELANGGAN ABC INV-2026-0001,,1500000\n2026-10-01,Beli Bensin SPBU Pertamina,200000,\n2026-10-02,SETORAN TUNAI CABANG JKT,,5000000\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "contoh_mutasi_bank.csv";
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 3000);
  };

  const applyItems = async (rowsToApply, { isAuto = false } = {}) => {
    const items = rowsToApply.map((r) => ({
      row_idx: r.row_idx, date: r.date, description: r.description,
      amount: r.amount, direction: r.direction,
      invoice_id: r.match?.invoice_id || null,
    }));
    const r = await api.post("/ai/bank-match/apply", { cash_bank_id: cbId, items });
    const totalAmt = rowsToApply.reduce((s, x) => s + x.amount, 0);
    if (isAuto) {
      setAutoApplied({ count: r.data.applied, failed: r.data.failed, total_amount: totalAmt, items: r.data.details?.applied || [] });
    }
    return r.data;
  };

  const analyze = async () => {
    if (!cbId) { toast.error("Pilih akun Kas/Bank dulu"); return; }
    if (!file) { toast.error("Pilih file mutasi bank (CSV/XLSX)"); return; }
    setAnalyzing(true);
    setResult(null); setSelected({}); setAppliedSummary(null); setAutoApplied(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}/ai/bank-match/analyze?cash_bank_id=${cbId}`, {
        method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (!resp.ok) {
        if (resp.status === 403) { setForbidden(true); throw new Error(data.detail || "Fitur ini memerlukan paket Pro/Business"); }
        throw new Error(data.detail || "Gagal menganalisis file");
      }
      let rows = data.rows || [];
      // Mode Auto-Apply: match confidence >= 95% langsung diterapkan tanpa konfirmasi
      if (autoApply) {
        const hiConf = rows.filter((r) => r.match && r.match.confidence >= 0.95);
        if (hiConf.length) {
          try {
            await applyItems(hiConf, { isAuto: true });
            const doneIdx = new Set(hiConf.map((r) => r.row_idx));
            rows = rows.filter((r) => !doneIdx.has(r.row_idx));
            toast.success(`Auto-Apply: ${hiConf.length} transaksi confidence ≥95% langsung dicatat & dijurnal`);
          } catch (e) {
            toast.error("Auto-apply gagal: " + (e?.response?.data?.detail || e.message));
          }
        }
      }
      setResult({
        ...data, rows,
        rows_total: rows.length,
        matched_count: rows.filter((r) => r.match).length,
        unmatched_count: rows.filter((r) => !r.match).length,
      });
      const pre = {};
      rows.forEach((r) => { if (r.match) pre[r.row_idx] = true; });
      setSelected(pre);
      toast.success(`${data.rows_total} baris dibaca — ${data.matched_count} cocok dengan faktur`);
    } catch (e) { toast.error(e.message); }
    finally { setAnalyzing(false); }
  };

  const selCount = useMemo(() => Object.values(selected).filter(Boolean).length, [selected]);
  const selAmount = useMemo(() => {
    if (!result) return 0;
    return (result.rows || []).reduce((s, r) => (selected[r.row_idx] ? s + r.amount : s), 0);
  }, [result, selected]);

  const toggle = (idx) => setSelected((s) => ({ ...s, [idx]: !s[idx] }));
  const toggleAll = (on) => {
    const next = {};
    (result?.rows || []).forEach((r) => { next[r.row_idx] = on; });
    setSelected(next);
  };

  const apply = async () => {
    if (!result) return;
    const rowsToApply = result.rows.filter((r) => selected[r.row_idx]);
    if (!rowsToApply.length) { toast.error("Pilih minimal 1 baris"); return; }
    if (!window.confirm(`Terapkan ${rowsToApply.length} transaksi? Saldo kas/bank & faktur terkait akan diperbarui dan jurnal otomatis diposting.`)) return;
    setApplying(true);
    try {
      const data = await applyItems(rowsToApply);
      setAppliedSummary(data);
      toast.success(`${data.applied} transaksi berhasil dicatat${data.failed ? `, ${data.failed} gagal` : ""}`);
      setResult(null); setSelected({}); setFile(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menerapkan"); }
    finally { setApplying(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl space-y-6" data-testid="bank-recon-page">
      <div>
        <h1 className="font-serif-display text-4xl font-medium text-slate-900 flex items-center gap-2">
          <ArrowsLeftRight size={28} weight="duotone" className="text-sky-600" />
          Rekonsiliasi Bank AI
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Upload mutasi rekening (BCA/Mandiri/BRI/BNI) — AI mencocokkan tiap baris dengan faktur terbuka. Anda tinggal konfirmasi.
        </p>
      </div>

      {forbidden && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-4 text-sm">
          Fitur Rekonsiliasi Bank AI memerlukan paket <b>Pro / Business</b>. Upgrade di menu Billing.
        </div>
      )}

      {/* Step 1: Upload */}
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><UploadSimple size={16} />1. Upload Mutasi Bank</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-64">
            <label className="text-xs text-slate-500">Akun Kas/Bank tujuan</label>
            <select className="w-full border border-slate-300 rounded px-2.5 py-2 text-sm" value={cbId}
                    onChange={(e) => setCbId(e.target.value)} data-testid="recon-account-select">
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name} ({a.code})</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-56">
            <label className="text-xs text-slate-500">File mutasi (.csv / .xlsx)</label>
            <input type="file" accept=".csv,.xlsx,.xls"
                   onChange={(e) => setFile(e.target.files?.[0] || null)}
                   className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-slate-900 file:text-white hover:file:bg-slate-800"
                   data-testid="recon-file-input" />
          </div>
          <Button onClick={analyze} disabled={analyzing || !file || !cbId}
                  className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="recon-analyze-btn">
            <Sparkle size={14} className="mr-1" />{analyzing ? "Menganalisis…" : "Analisis dengan AI"}
          </Button>
          <Button variant="outline" onClick={downloadSample} data-testid="recon-sample-btn">
            <FileArrowDown size={14} className="mr-1" />Contoh Format
          </Button>
        </div>
        <label className="mt-3 flex items-center gap-2 text-xs text-slate-700 bg-emerald-50 border border-emerald-100 rounded px-3 py-2 cursor-pointer" data-testid="recon-autoapply-toggle">
          <input type="checkbox" checked={autoApply} onChange={(e) => setAutoApply(e.target.checked)}
                 data-testid="recon-autoapply-checkbox" />
          <span>
            <b>Mode Auto-Apply (≥95%)</b> — baris dengan kecocokan sangat tinggi (nominal + nama partner persis)
            langsung dicatat &amp; dijurnal <b>tanpa konfirmasi</b>. Sisanya tetap Anda tinjau manual di bawah.
          </span>
        </label>
        <p className="text-[11px] text-slate-400 mt-2">
          Format dikenali otomatis: kolom Tanggal, Keterangan, Debet/Kredit (atau Amount). Unduh contoh format bila ragu.
        </p>
      </section>

      {/* Auto-applied panel */}
      {autoApplied && (
        <section className="bg-emerald-600 text-white rounded-md p-4 flex items-start gap-3" data-testid="recon-autoapplied">
          <Sparkle size={20} weight="fill" className="mt-0.5 shrink-0" />
          <div className="flex-1 text-sm">
            <b>Auto-Apply selesai:</b> {autoApplied.count} transaksi (confidence ≥95%) senilai{" "}
            <b>{formatIDR(autoApplied.total_amount)}</b> langsung dicatat &amp; dijurnal.
            {autoApplied.failed > 0 && <span className="text-emerald-100"> {autoApplied.failed} gagal.</span>}
            {result && result.rows?.length > 0 && (
              <div className="text-emerald-100 text-xs mt-1">
                {result.rows.length} baris lain menunggu tinjauan manual Anda di bawah.
              </div>
            )}
          </div>
        </section>
      )}

      {/* Step 2: Result table */}
      {analyzing && <Skeleton className="h-64" />}

      {result && (
        <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="recon-result">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Bank size={16} />2. Tinjau Saran AI
              <span className="text-xs font-normal text-slate-500">
                {result.rows_total} baris · <span className="text-emerald-600">{result.matched_count} cocok</span> · {result.unmatched_count} tanpa match
              </span>
            </h3>
            <div className="flex items-center gap-2 text-xs">
              <button className="text-sky-600 hover:underline" onClick={() => toggleAll(true)} data-testid="recon-select-all">Pilih semua</button>
              <span className="text-slate-300">|</span>
              <button className="text-slate-500 hover:underline" onClick={() => toggleAll(false)}>Kosongkan</button>
            </div>
          </div>

          <div className="overflow-x-auto border border-slate-100 rounded">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="p-2 w-8"></th>
                  <th className="p-2 text-left w-24">Tanggal</th>
                  <th className="p-2 text-left">Keterangan Mutasi</th>
                  <th className="p-2 text-right w-28">Nominal</th>
                  <th className="p-2 text-center w-16">Arah</th>
                  <th className="p-2 text-left">Saran AI</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(result.rows || []).map((r) => (
                  <tr key={r.row_idx} className={selected[r.row_idx] ? "bg-sky-50/50" : ""}
                      data-testid={`recon-row-${r.row_idx}`}>
                    <td className="p-2 text-center">
                      <input type="checkbox" checked={!!selected[r.row_idx]} onChange={() => toggle(r.row_idx)}
                             data-testid={`recon-check-${r.row_idx}`} />
                    </td>
                    <td className="p-2 whitespace-nowrap">{r.date}</td>
                    <td className="p-2 max-w-md truncate" title={r.description}>{r.description}</td>
                    <td className="p-2 text-right tabular-nums font-medium">{formatIDR(r.amount)}</td>
                    <td className="p-2 text-center">
                      {r.direction === "in"
                        ? <span className="inline-flex items-center gap-1 text-emerald-600"><ArrowDown size={12} />Masuk</span>
                        : <span className="inline-flex items-center gap-1 text-rose-600"><ArrowUp size={12} />Keluar</span>}
                    </td>
                    <td className="p-2">
                      {r.match ? (
                        <div className="flex items-center gap-1.5">
                          <ConfBadge conf={r.match.confidence} />
                          <span className="font-medium text-slate-800">{r.match.invoice_number}</span>
                          <span className="text-slate-500">— {r.match.partner_name}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400 italic">
                          Tanpa match — akan dicatat sebagai {r.direction === "in" ? "uang masuk" : "uang keluar"} umum
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
            <div className="text-sm text-slate-600" data-testid="recon-selected-summary">
              <b>{selCount}</b> baris dipilih · total <b>{formatIDR(selAmount)}</b>
            </div>
            <Button onClick={apply} disabled={applying || !selCount}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="recon-apply-btn">
              <CheckCircle size={14} className="mr-1" />
              {applying ? "Menerapkan…" : `Terapkan ${selCount} Transaksi`}
            </Button>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">
            Penerapan akan: mencatat transaksi kas/bank, memposting jurnal otomatis, dan memperbarui sisa tagihan faktur yang cocok.
          </p>
        </section>
      )}

      {/* Step 3: Applied summary */}
      {appliedSummary && (
        <section className="bg-emerald-50 border border-emerald-200 rounded-md p-5" data-testid="recon-applied">
          <h3 className="font-semibold text-emerald-900 flex items-center gap-2">
            <CheckCircle size={16} weight="fill" />Rekonsiliasi Selesai
          </h3>
          <div className="text-sm text-emerald-800 mt-2">
            <b>{appliedSummary.applied}</b> transaksi berhasil dicatat & dijurnal
            {appliedSummary.failed > 0 && <span className="text-rose-600"> · {appliedSummary.failed} gagal</span>}
          </div>
          {(appliedSummary.details?.failed || []).slice(0, 5).map((f, i) => (
            <div key={i} className="text-xs text-rose-600 mt-1 flex items-center gap-1">
              <Warning size={12} /> Baris {f.row_idx}: {f.reason}
            </div>
          ))}
          <Button variant="outline" size="sm" className="mt-3" onClick={() => setAppliedSummary(null)}>
            <Trash size={12} className="mr-1" />Tutup & Rekonsiliasi Lagi
          </Button>
        </section>
      )}
    </div>
  );
}
